import { auth } from './firebase.js';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

export class ApiError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  let token = localStorage.getItem('edurender_master_token');

  // If Firebase currentUser exists, obtain fresh token seamlessly
  if (auth.currentUser) {
    try {
      token = await auth.currentUser.getIdToken();
      localStorage.setItem('edurender_master_token', token);
    } catch {
      // fallback to cached token
    }
  }

  if (!token) {
    token = 'dev-token-super-admin';
  }

  const headers: Record<string, string> = {
    ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // If we have an active user, try a forced token refresh and retry once
    if (auth.currentUser) {
      try {
        const freshToken = await auth.currentUser.getIdToken(true);
        localStorage.setItem('edurender_master_token', freshToken);
        const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` };
        const retryRes = await fetch(url, { ...options, headers: retryHeaders });
        if (retryRes.ok) {
          return retryRes.json();
        }
      } catch {
        // Fall through
      }
    }

    if (window.location.pathname !== '/login') {
      localStorage.removeItem('edurender_master_token');
    }
    throw new ApiError('Sessão expirada. Faça login novamente.', 401);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.message || `Erro HTTP ${response.status}: ${response.statusText}`;
    throw new ApiError(message, response.status);
  }

  return response.json();
}

export const api = {
  // Auth
  getMe: () => request<{ user: any }>('/auth/me'),

  // Dashboard
  getMasterDashboard: () => request<any>('/master/dashboard'),

  // Customers
  getCustomers: (params?: { status?: string; q?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.q) query.append('q', params.q);
    return request<{ items: any[]; total: number }>(`/master/customers?${query.toString()}`);
  },
  getCustomer: (id: string) => request<any>(`/master/customers/${id}`),
  createCustomer: (data: any) =>
    request<any>('/master/customers', { method: 'POST', body: JSON.stringify(data) }),
  updateCustomer: (id: string, data: any) =>
    request<any>(`/master/customers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  suspendCustomer: (id: string) =>
    request<any>(`/master/customers/${id}/suspend`, { method: 'POST' }),
  reactivateCustomer: (id: string) =>
    request<any>(`/master/customers/${id}/reactivate`, { method: 'POST' }),
  deleteCustomer: (id: string) =>
    request<any>(`/master/customers/${id}`, { method: 'DELETE' }),

  // Plans
  getPlans: () => request<{ items: any[] }>('/master/plans'),
  getPlan: (id: string) => request<any>(`/master/plans/${id}`),
  createPlan: (data: any) =>
    request<any>('/master/plans', { method: 'POST', body: JSON.stringify(data) }),
  updatePlan: (id: string, data: any) =>
    request<any>(`/master/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePlan: (id: string) =>
    request<any>(`/master/plans/${id}`, { method: 'DELETE' }),

  // Subscriptions
  getSubscriptions: () => request<{ items: any[]; total: number }>('/master/subscriptions'),
  createSubscription: (data: any) =>
    request<any>('/master/subscriptions', { method: 'POST', body: JSON.stringify(data) }),
  activateSubscription: (id: string) =>
    request<any>(`/master/subscriptions/${id}/activate`, { method: 'POST' }),
  deactivateSubscription: (id: string) =>
    request<any>(`/master/subscriptions/${id}/deactivate`, { method: 'POST' }),
  cancelSubscription: (id: string) =>
    request<any>(`/master/subscriptions/${id}/cancel`, { method: 'POST' }),
  deleteSubscription: (id: string) =>
    request<any>(`/master/subscriptions/${id}`, { method: 'DELETE' }),

  // Quota
  getCustomerQuota: (id: string) => request<any>(`/master/customers/${id}/quota`),
  adjustQuota: (id: string, data: { bonus: number; adjustment: number; reason: string }) =>
    request<any>(`/master/customers/${id}/quota/adjust`, { method: 'POST', body: JSON.stringify(data) }),

  // Global Jobs
  getJobs: (status?: string) =>
    request<{ items: any[]; total: number }>(`/master/jobs${status ? `?status=${status}` : ''}`),
  cancelJob: (id: string) => request<any>(`/master/jobs/${id}/cancel`, { method: 'POST' }),

  // Customer Users & Password Management
  getCustomerUsers: (customerId: string) =>
    request<{ items: any[]; total: number }>(`/master/customers/${customerId}/users`),
  createCustomerUser: (customerId: string, data: any) =>
    request<any>(`/master/customers/${customerId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateUserPassword: (userId: string, password: string) =>
    request<any>(`/master/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ password }),
    }),

  // Google Drive Transfers V2.0 Monitoring
  getMasterDriveStats: () =>
    request<import('../types/index.js').DriveTransferMasterStats>('/master/google-drive/stats'),
  getMasterDriveTransfers: (params?: { customerId?: string; status?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.customerId) q.append('customerId', params.customerId);
    if (params?.status) q.append('status', params.status);
    if (params?.limit) q.append('limit', String(params.limit));
    return request<{ items: import('../types/index.js').MasterTransferBatch[]; total: number }>(
      `/master/google-drive/transfers?${q.toString()}`
    );
  },

  // YouTube Integration V1.0 Monitoring
  getMasterYouTubeStats: () =>
    request<import('../types/index.js').YouTubeTransferMasterStats>('/master/youtube/stats'),
  getMasterYouTubeTransfers: (params?: { customerId?: string; status?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.customerId) q.append('customerId', params.customerId);
    if (params?.status) q.append('status', params.status);
    if (params?.limit) q.append('limit', String(params.limit));
    return request<{ items: import('../types/index.js').MasterTransferBatch[]; total: number }>(
      `/master/youtube/transfers?${q.toString()}`
    );
  },
};
